/* Yarn & global trade — a port of `Yarn & Global Trade.dc.html`.
 *
 * The design reads one object and does all its arithmetic in the page; this
 * does the same, on the same object, served from the database. `build` is
 * the design's `renderVals` moved across as it stands — the same trailing
 * twelve months, unit values, scales and rankings — so a figure here can be
 * checked against the design line for line. Only three things differ: grid
 * lines and the first line colour take the theme's `--c-grid` and `--c-ink`
 * rather than fixed colours, so the charts survive the dark theme; the
 * page's two tabs sit at the top of the page rather than in the header,
 * which belongs to the portal; and price files added under "Yarn price
 * trends" go to the server rather than this browser's storage, so everyone
 * sees them.
 *
 * The design serves two pages from one file — `?view=cotton` is the cotton
 * side of the global trade alone, `?embed=1` drops the header — and those
 * are the `view` and `embed` props here. Embedded, this renders only the
 * page's sections, for a host page that supplies its own `<main>`.
 */

import { useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { MAIN_STYLE, Subline } from '../App'
import { trade } from '../lib/api_trade'
import type { Flow, TradeCell, TradePayload, TradeRow, YarnPriceSeries } from '../lib/api_trade'
import { useAsync } from '../lib/useAsync'
import { useCan } from '../lib/auth'
import { Blueprint, Empty, ErrorBox, LoadElsewhere, Loading } from '../components/ui'

export type TradeView = 'yarn' | 'cotton'

/** How many rows the ranking tables show. */
const TOP_N = 12
/** Raw-cotton tiles also carry lakh bales, at the Indian 170 kg bale. */
const SHOW_BALES = true

/* RBI reference monthly average USD/INR; recent months are provisional. */
const FX: Record<number, number[]> = {
  2018: [63.7, 64.4, 65.0, 65.6, 67.5, 67.8, 68.6, 69.6, 72.2, 73.6, 72.0, 70.7],
  2019: [70.7, 71.2, 69.5, 69.4, 69.8, 69.5, 68.8, 71.1, 71.3, 70.9, 71.4, 71.2],
  2020: [71.3, 71.5, 74.4, 76.2, 75.7, 75.8, 74.9, 74.6, 73.5, 73.5, 74.2, 73.6],
  2021: [73.1, 72.7, 72.7, 74.5, 73.4, 73.4, 74.5, 74.2, 73.6, 74.9, 74.5, 75.6],
  2022: [74.4, 75.0, 76.2, 76.2, 77.3, 78.0, 79.6, 79.6, 80.2, 82.4, 81.7, 82.7],
  2023: [81.8, 82.6, 82.3, 82.0, 82.3, 82.1, 82.0, 82.8, 83.0, 83.2, 83.3, 83.3],
  2024: [83.1, 83.0, 83.0, 83.4, 83.5, 83.4, 83.6, 83.9, 83.8, 84.1, 84.4, 84.8],
  2025: [86.2, 87.0, 86.6, 85.6, 85.3, 86.1, 86.3, 87.5, 88.2, 88.5, 88.8, 89.0],
  2026: [89.2, 89.3, 89.0, 88.8, 88.6, 88.5, 88.4, 88.3],
}

function fx(period: string): number {
  const y = +period.slice(0, 4)
  const m = +period.slice(4, 6) || 1
  const row = FX[y]
  if (row) return row[m - 1] || row[row.length - 1]
  const yrs = Object.keys(FX).map(Number)
  const near = y < Math.min(...yrs) ? Math.min(...yrs) : Math.max(...yrs)
  const r = FX[near]
  return r[Math.min(m - 1, r.length - 1)]
}

// ------------------------------------------------------------- formatting
const ok = (v: number | null | undefined): v is number =>
  v !== null && v !== undefined && isFinite(v)

function n(v: number | null | undefined, d: number): string {
  if (!ok(v)) return '–'
  return Number(v).toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d })
}
const inr = (v: number | null, d = 0) => (ok(v) ? '₹' + n(v, d) : '–')
const kt = (kg: number | null | undefined) => (!kg ? '–' : n(kg / 1e6, kg >= 1e8 ? 0 : 1) + ' kt')
const usd = (v: number | null | undefined) =>
  (!v ? '–' : v >= 1e9 ? '$' + n(v / 1e9, 2) + 'B' : '$' + n(v / 1e6, 0) + 'M')
const perkg = (v: number, kg: number | null) => (kg && kg > 0 ? '$' + n(v / kg, 2) : '–')
const bales = (kg: number | null) => (kg ? n(kg / 170 / 1e5, 2) + ' lakh bales' : '–')
const pct = (now: number | null, then: number | null) =>
  (!ok(now) || !then || !isFinite(now / then) ? null : (now / then - 1) * 100)

const UP = 'var(--color-accent-800)'
const DOWN = '#8a5b3a'

function deltaTxt(p: number | null, invert = false): { delta: string; tone: string } {
  if (!ok(p)) return { delta: '', tone: 'var(--color-neutral-600)' }
  const good = invert ? p < 0 : p > 0
  return { delta: (p >= 0 ? '▲ ' : '▼ ') + n(Math.abs(p), 1) + '% yoy', tone: good ? UP : DOWN }
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const month = (p: string) => (p.length === 6 ? MONTHS[+p.slice(4) - 1] + ' ' + p.slice(0, 4) : p)

function shiftMonth(p: string, back: number): string {
  let y = +p.slice(0, 4)
  let m = +p.slice(4) - back
  while (m <= 0) { m += 12; y-- }
  return String(y * 100 + m).padStart(6, '0')
}

// ------------------------------------------------------------------ scales
function scale(lo: number, hi: number, want = 4) {
  if (hi === lo) hi = lo + 1
  const raw = (hi - lo) / want
  const mag = Math.pow(10, Math.floor(Math.log10(raw || 1)))
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) || mag * 10
  const start = Math.floor(lo / step) * step
  const end = start + step * Math.max(1, Math.ceil((hi - start) / step))
  const vals: number[] = []
  for (let v = start; v <= end + step * 1e-9; v += step) vals.push(+v.toFixed(6))
  return { lo: start, hi: end, vals }
}

/** A line broken wherever a value is missing, as runs of "x,y" points. */
function segsOf(vals: (number | null)[], xOf: (i: number) => number, yOf: (v: number) => number) {
  const segs: string[] = []
  let cur: string[] = []
  vals.forEach((v, i) => {
    if (!ok(v)) { if (cur.length > 1) segs.push(cur.join(' ')); cur = []; return }
    cur.push(xOf(i).toFixed(1) + ',' + yOf(v).toFixed(1))
  })
  if (cur.length > 1) segs.push(cur.join(' '))
  return segs
}

// ------------------------------------------------------------- page state
type Metric = 'volume' | 'value' | 'price'
type Freq = 'annual' | 'monthly'
interface State {
  tab: 'trends' | 'world'; span: number; group: string; metric: Metric; freq: Freq
  pYear: string | null; wCmd: string; wYear: string | null
}

const PW = 1000
const PH = 320

interface Line { name: string; color: string; dash: string; last?: string; segs: string[] }
interface KpiTile { label: string; value: string; delta: string; tone: string; note: string }
interface RankRow {
  name: string; kg: string; usd: string; uv: string; share: string; pct: number
  bg?: string; weight?: string; yoy?: string; tone?: string
}

/** The design's `renderVals`: everything the page shows, from the payload
 *  and the controls. */
function build(d: TradePayload, S: State, view: TradeView) {
  const EMPTY: TradeCell = { kg: 0, usd: 0, uw: 0 }
  const at = (bucket: 'annual' | 'monthly', group: string, flow: Flow, period: string): TradeCell =>
    ((d[bucket][group] || {})[flow] || {})[period] || EMPTY
  const uv = (v: TradeCell) => (v.kg && v.kg > 0 ? (v.uw || v.usd) / v.kg : null)
  const periods = (bucket: 'annual' | 'monthly', group: string, flow: Flow) =>
    Object.keys((d[bucket][group] || {})[flow] || {}).sort()
  const hasSeries = (bucket: 'annual' | 'monthly', group: string, flow: Flow) =>
    periods(bucket, group, flow).some((p) => at(bucket, group, flow, p).usd > 0)
  const ttm = (group: string, flow: Flow, end: string) => {
    const out = { kg: 0, usd: 0, uw: 0, gaps: 0 }
    let y = +end.slice(0, 4)
    let m = +end.slice(4)
    for (let i = 0; i < 12; i++) {
      const p = String(y * 100 + m).padStart(6, '0')
      const v = at('monthly', group, flow, p)
      if (v.kg === null) out.gaps++
      else { out.kg += v.kg; out.uw += (v.uw || v.usd) }
      out.usd += v.usd
      m--
      if (m === 0) { m = 12; y-- }
    }
    return out
  }

  // Sewing thread (HS 5204) is collected but not shown, as in the design; the
  // cotton view keeps the cotton headings (5201–5203) and the yarn view the
  // rest.
  const isCot = (x: { codes: string[] }) => x.codes.some((c) => /^520[123]/.test(c))
  const groups = d.groups.filter((x) =>
    !x.codes.includes('5204') && (view === 'cotton' ? isCot(x) : !isCot(x)))
  const g = groups.find((x) => x.key === S.group) || groups[0]
  const LAST_M = d.latestMonth
  const MIRROR = d.mirrorLast
  const allMonths = (() => {
    const s = new Set<string>()
    groups.forEach((gg) => (['X', 'M'] as Flow[]).forEach((f) =>
      periods('monthly', gg.key, f).forEach((p) => s.add(p))))
    return [...s].sort().filter((p) => p <= LAST_M)
  })()
  const years = (() => {
    const s = new Set<string>()
    groups.forEach((gg) => (['X', 'M'] as Flow[]).forEach((f) =>
      periods('annual', gg.key, f).forEach((p) => s.add(p))))
    return [...s].sort()
  })()
  const LAST_Y = years[years.length - 1]
  const PREV_Y = years[years.length - 2]
  const idx = d.cottonIndex.map((r) => [r[0].replace('M', ''), r[1]] as [string, number])
  const yr = (k: string, f: Flow, y: string) => at('annual', k, f, y)

  const C = { X: 'var(--c-bar3)', M: 'var(--c-bar1)' }

  /* ---------------- KPI strip ---------------- */
  const now = ttm('yarn', 'X', LAST_M)
  const was = ttm('yarn', 'X', shiftMonth(LAST_M, 12))
  const rawM = yr('raw', 'M', LAST_Y)
  const rawMw = yr('raw', 'M', PREV_Y)
  const rawX = yr('raw', 'X', LAST_Y)
  const rawXw = yr('raw', 'X', PREV_Y)
  const yarnY = yr('yarn', 'X', LAST_Y)
  const yarnYw = yr('yarn', 'X', PREV_Y)
  const iNow = idx[idx.length - 1]
  const iWas = idx[idx.length - 13]
  const diff = (a: number | null, b: number | null) => (ok(a) && ok(b) ? a - b : null)
  const spread = diff(uv(yarnY), uv(rawM))
  const spreadW = diff(uv(yarnYw), uv(rawMw))
  const window12 = month(shiftMonth(LAST_M, 11)) + ' – ' + month(LAST_M)

  const wYears = Object.keys(d.world).sort()
  const filed = (y: string, f: Flow) => ((d.worldMeta[y] || {})[S.wCmd] || {})[f] || 0
  const wYear = S.wYear || wYears.slice().sort((a, b) => filed(b, 'M') - filed(a, 'M'))[0]
  const wGrab = (flow: Flow): TradeRow[] => (((d.world[wYear] || {})[S.wCmd] || {})[flow] || [])
  const wx = wGrab('X')
  const wm = wGrab('M')
  const wxTot = wx.reduce((a, r) => a + r.usd, 0)
  const wmTot = wm.reduce((a, r) => a + r.usd, 0)
  const indiaX = wx.find((r) => r.code === 699)
  const cmdName = S.wCmd === '5201' ? 'raw cotton (HS 5201)' : 'cotton yarn (HS 5205)'

  let kpis: KpiTile[]
  if (S.tab === 'trends') {
    kpis = [
      { label: 'Cotton A Index', value: iNow ? '$' + n(iNow[1], 2) + '/kg' : '–',
        note: iNow ? 'World lint benchmark · ' + month(iNow[0]) : 'not on file',
        ...deltaTxt(iNow && iWas ? pct(iNow[1], iWas[1]) : null) },
      { label: 'Yarn exports', value: kt(now.kg),
        note: usd(now.usd) + ' · ' + window12, ...deltaTxt(pct(now.kg, was.kg)) },
      { label: 'Yarn realisation', value: perkg(now.uw || now.usd, now.kg) + '/kg',
        note: 'Export unit value, trailing 12 months', ...deltaTxt(pct(uv(now), uv(was))) },
      { label: 'Conversion spread', value: ok(spread) ? '$' + n(spread, 2) + '/kg' : '–',
        note: 'Yarn price less lint cost · ' + LAST_Y, ...deltaTxt(pct(spread, spreadW)) },
      { label: 'Raw cotton imports', value: kt(rawM.kg),
        note: (SHOW_BALES ? bales(rawM.kg) + ' · ' : '') + LAST_Y + ', mirror data',
        ...deltaTxt(pct(rawM.kg, rawMw.kg), true) },
      { label: 'Raw cotton exports', value: kt(rawX.kg),
        note: (SHOW_BALES ? bales(rawX.kg) + ' · ' : '') + LAST_Y,
        ...deltaTxt(pct(rawX.kg, rawXw.kg)) },
    ]
  } else {
    const topM = wm[0]
    const topX = wx[0]
    const plain = 'var(--color-neutral-700)'
    kpis = [
      { label: 'India · world share', value: indiaX ? n(indiaX.usd / wxTot * 100, 1) + '%' : '–',
        delta: indiaX ? 'rank ' + (wx.indexOf(indiaX) + 1) + ' of ' + wx.length : '', tone: UP,
        note: 'of listed ' + cmdName + ' exports, ' + wYear },
      { label: 'India export value', value: indiaX ? usd(indiaX.usd) : '–',
        delta: indiaX ? perkg(indiaX.usd, indiaX.kg) + '/kg realised' : '', tone: plain,
        note: indiaX ? kt(indiaX.kg) + ' shipped' : 'not filed for ' + wYear },
      { label: 'Largest exporter', value: topX ? topX.name : '–', delta: topX ? usd(topX.usd) : '',
        tone: plain, note: topX ? n(topX.usd / wxTot * 100, 1) + '% of listed exports' : '' },
      { label: 'Largest importer', value: topM ? topM.name : '–', delta: topM ? usd(topM.usd) : '',
        tone: plain, note: topM ? n(topM.usd / wmTot * 100, 1) + '% of listed imports' : '' },
      { label: 'Reporters filed', value: filed(wYear, 'X') + ' / ' + filed(wYear, 'M'), delta: '',
        tone: plain, note: 'exporters / importers with ' + wYear + ' returns' },
      { label: 'Listed world trade', value: usd(wxTot), delta: '', tone: plain,
        note: cmdName + ' exports on the board' },
    ]
  }

  /* ---------------- price chart ---------------- */
  const lastIdx = idx.length ? idx[idx.length - 1][0] : LAST_M
  const cap = LAST_M > lastIdx ? LAST_M : lastIdx
  const pMonths = [...new Set([...idx.map((r) => r[0]), ...allMonths])]
    .filter((p) => p <= cap).sort().slice(-S.span)
  const unitSeries = (k: string, f: Flow) => pMonths.map((p) => {
    if (f === 'M' && p > MIRROR) return null
    const v = at('monthly', k, f, p)
    return v.kg && v.kg > 20000 ? uv(v) : null
  })
  const inrSeries = (k: string, f: Flow) =>
    unitSeries(k, f).map((v, i) => (v === null ? null : v * fx(pMonths[i])))
  const pSeries = [{ name: 'India yarn export price', color: C.X, dash: '0', v: inrSeries('yarn', 'X') }]
  const pAll = pSeries.flatMap((s) => s.v.filter(ok))
  const pSc = pAll.length ? scale(Math.min(...pAll) * 0.92, Math.max(...pAll) * 1.04, 4) : scale(0, 1, 4)
  const pxOf = (i: number) => (pMonths.length < 2 ? PW / 2 : i / (pMonths.length - 1) * PW)
  const pyOf = (v: number) => PH - (v - pSc.lo) / (pSc.hi - pSc.lo) * PH
  const lastOf = (vals: (number | null)[]) => {
    for (let i = vals.length - 1; i >= 0; i--) if (ok(vals[i])) return vals[i]
    return null
  }
  const price = {
    yLabels: pSc.vals.map((v) => inr(v, 0)),
    gridY: pSc.vals.map((v) => +pyOf(v).toFixed(1)),
    xLabels: [0, 1, 2, 3, 4, 5].map((i) => month(pMonths[Math.round(i / 5 * (pMonths.length - 1))] || '')),
    lines: pSeries.map<Line>((s) => {
      const last = lastOf(s.v)
      return { name: s.name, color: s.color, dash: s.dash,
        last: last === null ? '–' : inr(last, 0) + '/kg', segs: segsOf(s.v, pxOf, pyOf) }
    }),
  }

  /* ---------------- volume chart ---------------- */
  const bucket = S.freq === 'annual' ? 'annual' : 'monthly'
  const xs = S.freq === 'annual' ? years : allMonths
  const pick = (f: Flow, p: string): number | null => {
    if (bucket === 'monthly' && f === 'M' && p > MIRROR) return null
    const v = at(bucket, S.group, f, p)
    if (S.metric === 'volume') return v.kg === null ? null : v.kg / 1e6
    if (S.metric === 'value') return v.usd / 1e6
    return v.kg && v.kg > 20000 ? uv(v) : null
  }
  const flows = ([
    { key: 'X', name: 'Exports · ' + g.label, color: C.X, dash: '0' },
    { key: 'M', name: 'Imports · ' + g.label, color: C.M, dash: '5 3' },
  ] as { key: Flow; name: string; color: string; dash: string }[])
    .filter((f) => hasSeries(bucket, S.group, f.key))
  const vAll = flows.flatMap((f) => xs.map((p) => pick(f.key, p)).filter(ok))
  const vLo = S.metric === 'price' ? Math.min(...vAll) * 0.9 : 0
  const vSc = scale(vAll.length ? vLo : 0, vAll.length ? Math.max(...vAll) * 1.04 : 1, 4)
  const vyOf = (v: number) => PH - (v - vSc.lo) / (vSc.hi - vSc.lo) * PH
  const fmtV = S.metric === 'price' ? (v: number) => '$' + n(v, 2)
    : S.metric === 'value' ? (v: number) => '$' + n(v, 0) + 'M' : (v: number) => n(v, 0) + ' kt'
  const fmtCell = (f: Flow, p: string) => {
    const v = at(bucket, S.group, f, p)
    return kt(v.kg) + ' · ' + usd(v.usd) + ' · ' + perkg(v.uw || v.usd, v.kg) + '/kg'
  }
  const bars: { x: number; y: number; w: number; h: number; color: string; tip: string }[] = []
  let volLines: Line[] = []
  let volX: string[]
  if (S.freq === 'annual') {
    const cw = PW / xs.length
    const bw = Math.min(26, (cw - 14) / Math.max(1, flows.length))
    xs.forEach((p, i) => {
      flows.forEach((f, j) => {
        const v = pick(f.key, p)
        if (!ok(v)) return
        const y = vyOf(v)
        bars.push({
          x: +(i * cw + cw / 2 - (flows.length * bw) / 2 + j * bw).toFixed(1), y: +y.toFixed(1),
          w: +bw.toFixed(1), h: +Math.max(1, PH - y).toFixed(1), color: f.color,
          tip: p + ' · ' + f.name + ' — ' + fmtCell(f.key, p),
        })
      })
    })
    volX = xs
  } else {
    const mxOf = (i: number) => (xs.length < 2 ? PW / 2 : i / (xs.length - 1) * PW)
    volLines = flows.map((f) => ({ name: f.name, color: f.color, dash: f.dash,
      segs: segsOf(xs.map((p) => pick(f.key, p)), mxOf, vyOf) }))
    volX = [0, 1, 2, 3, 4, 5, 6, 7].map((i) => month(xs[Math.round(i / 7 * (xs.length - 1))] || ''))
  }
  const vol = {
    yLabels: vSc.vals.map(fmtV),
    gridY: vSc.vals.map((v) => +vyOf(v).toFixed(1)),
    bars, lines: volLines, xLabels: volX,
    legend: flows.map((f) => {
      const last = S.freq === 'annual' ? pick(f.key, xs[xs.length - 1]) : lastOf(xs.map((p) => pick(f.key, p)))
      return { name: f.name, color: f.color, last: ok(last) ? fmtV(last) : '–' }
    }),
    title: g.label + ' · ' + (S.metric === 'volume' ? 'volumes' : S.metric === 'value' ? 'values' : 'realised price'),
    hint: g.hs + ' · ' + (S.freq === 'annual' ? 'calendar years ' + years[0] + '–' + LAST_Y
      : 'monthly, exports to ' + month(LAST_M) + ', imports to ' + month(MIRROR)),
    note: 'Exports are India’s own customs returns; imports are mirror data — what supplying countries report shipping to India.'
      + (S.freq === 'monthly' && !hasSeries('monthly', S.group, 'M') ? ' No monthly import mirror is collected for this line — see the annual view.' : '')
      + (S.metric === 'price' ? ' Unit value tracks the mix of counts and grades as much as the market.' : '')
      + (S.freq === 'monthly' ? ' The last few months of either series still move as returns come in.' : ''),
  }

  /* ---------------- annual table ---------------- */
  const annual = {
    title: g.label + ' by year',
    hint: g.hs + ' · exports, mirror imports and the gap between them',
    rows: years.slice().reverse().map((y) => {
      const x = yr(g.key, 'X', y)
      const m = yr(g.key, 'M', y)
      const xu = uv(x)
      const mu = uv(m)
      return { year: y, xkg: kt(x.kg), xusd: usd(x.usd), xuv: perkg(x.uw || x.usd, x.kg),
        mkg: kt(m.kg), musd: usd(m.usd), muv: perkg(m.uw || m.usd, m.kg),
        spread: xu && mu ? '$' + n(xu - mu, 2) : '–' }
    }),
  }

  /* ---------------- world tables ---------------- */
  const rankRows = (rows: TradeRow[], total: number): RankRow[] => {
    const top = rows.slice(0, TOP_N)
    const max = Math.max(1, ...top.map((r) => r.usd))
    return top.map((r) => ({
      name: r.name, kg: kt(r.kg), usd: usd(r.usd), uv: perkg(r.usd, r.kg),
      share: total ? n(r.usd / total * 100, 1) + '%' : '–',
      pct: +(r.usd / max * 100).toFixed(1),
      bg: r.code === 699 ? 'var(--color-accent-100)' : 'transparent',
      weight: r.code === 699 ? '700' : '400',
    }))
  }
  const world = {
    hint: cmdName + ', ranked by customs value · ' + wYear + ' · India highlighted',
    cmdTabs: view === 'cotton' ? [{ k: '5201', l: 'Raw cotton' }] : [{ k: '5205', l: 'Cotton yarn' }],
    years: wYears, year: wYear,
    exporters: rankRows(wx, wxTot),
    importers: rankRows(wm, wmTot),
    note: 'An attendance-limited table: ' + filed(wYear, 'X') + ' exporters and '
      + filed(wYear, 'M') + ' importers filed for ' + wYear
      + '. Countries that report late or not at all are simply absent — Bangladesh, India’s largest yarn customer, never appears, and China’s filing lands a year behind, so the newest year understates the demand side badly. Shares are shares of identified trade.',
  }

  /* ---------------- India partners ---------------- */
  const P = d.partners
  const pYears = [...new Set([...Object.keys(P.X || {}), ...Object.keys(P.M || {})])].sort()
  const pYear = S.pYear || pYears[pYears.length - 1]
  const prevP = pYears[pYears.indexOf(pYear) - 1]
  const grabP = (flow: Flow, y: string | undefined): TradeRow[] =>
    (y ? (((P[flow] || {})[y] || {})[S.group] || []) : [])
  const partnerRows = (flow: Flow): RankRow[] => {
    const rows = grabP(flow, pYear)
    const prev = grabP(flow, prevP)
    const total = rows.reduce((a, r) => a + r.usd, 0)
    const max = Math.max(1, ...rows.slice(0, TOP_N).map((r) => r.usd))
    return rows.slice(0, TOP_N).map((r) => {
      const before = prev.find((p) => p.code === r.code)
      const y = before ? pct(r.usd, before.usd) : null
      return { name: r.name, kg: kt(r.kg), usd: usd(r.usd), uv: perkg(r.usd, r.kg),
        share: total ? n(r.usd / total * 100, 1) + '%' : '–', pct: +(r.usd / max * 100).toFixed(1),
        yoy: y === null ? '' : (y >= 0 ? '▲ ' : '▼ ') + n(Math.abs(y), 0) + '%',
        tone: y === null ? 'transparent' : (y >= 0 ? UP : DOWN) }
    })
  }
  const partners = {
    hint: g.label + ' (' + g.hs + ') · ' + pYear + ' · ranked by customs value',
    destTitle: 'Export destinations · ' + pYear,
    srcTitle: 'Import origins · ' + pYear,
    years: pYears, year: pYear,
    dest: partnerRows('X'), src: partnerRows('M'),
    note: 'The $/kg column is what that partner actually paid or charged per kilo — the fastest read on where the fine-count business sits'
      + (prevP ? '; arrows compare with ' + prevP : '') + '. Origins are the supplying country’s own export filing, valued FOB at its border, so they sit below the landed cost at an Indian port.',
  }

  return {
    groups, kpis, price, vol, annual, world, partners,
    periodLine: 'India’s cotton and yarn trade against the world market — volumes, realised prices and trading partners. Exports through '
      + month(LAST_M) + ', mirror imports through ' + month(MIRROR) + ', annual through ' + LAST_Y
      + (iNow ? '; lint prices through ' + month(iNow[0]) + '.' : '.'),
    footerCaveat: 'Comtrade estimates some cells and revises recent months, so the newest points will move. '
      + d.droppedWeights + ' period(s) filed a net weight so far out of line with their neighbours that the weight was dropped and the point left as a gap; their value is still counted. '
      + 'A few reporters file value without net weight; their volume shows as a dash. Calendar years, not Indian financial years — and the cotton season runs October to September, which a calendar year cuts across.',
  }
}

// ------------------------------------------------------------------ pieces
const R: CSSProperties = { textAlign: 'right' }
const NUMR: CSSProperties = { textAlign: 'right', fontVariantNumeric: 'tabular-nums' }
const AXIS_LINE = '1px solid var(--color-neutral-500)'

/** A run of joined buttons, the design's segmented control. */
function Tabs<T extends string | number>({ items, value, onPick, joined = true }: {
  items: { k: T; l: string }[]; value: T; onPick: (k: T) => void; joined?: boolean
}) {
  return (
    <div style={{ display: 'flex', gap: joined ? 0 : 8, flexWrap: joined ? 'nowrap' : 'wrap' }}>
      {items.map((t) => (
        <button key={String(t.k)} type="button"
          className={`btn ${value === t.k ? 'btn-primary' : 'btn-secondary'}`}
          style={joined ? { marginRight: -1 } : undefined}
          onClick={() => onPick(t.k)}>{t.l}</button>
      ))}
    </div>
  )
}

function Control({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{
        fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase',
        color: 'var(--color-neutral-600)',
      }}>{label}</span>
      {children}
    </div>
  )
}

function PanelTop({ title, hint, controls }: { title: string; hint: string; controls?: ReactNode }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
      gap: 16, flexWrap: 'wrap',
    }}>
      <div>
        <h2 style={{ fontSize: 22, margin: 0 }}>{title}</h2>
        <div style={{ fontSize: 12, color: 'var(--color-neutral-600)', marginTop: 2 }}>{hint}</div>
      </div>
      {controls ? (
        <div className="no-print" style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>{controls}</div>
      ) : null}
    </div>
  )
}

/** The 1000 × 320 plot the design uses for both charts: HTML axes round a
 *  stretched SVG, so the lines scale and the labels do not. */
function Plot({ yLabels, gridY, xLabels, children }: {
  yLabels: string[]; gridY: number[]; xLabels: string[]; children: ReactNode
}) {
  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: '76px 1fr', gap: 8, marginTop: 14 }}>
        <div className="num" style={{
          display: 'flex', flexDirection: 'column-reverse', justifyContent: 'space-between',
          alignItems: 'flex-end', height: PH, margin: '-8px 0', fontSize: 11,
          color: 'var(--color-neutral-600)',
        }}>
          {yLabels.map((t, i) => <span key={i}>{t}</span>)}
        </div>
        <svg viewBox={`0 0 ${PW} ${PH}`} preserveAspectRatio="none" style={{
          width: '100%', height: PH, display: 'block', borderLeft: AXIS_LINE, borderBottom: AXIS_LINE,
        }}>
          {gridY.map((y, i) => (
            <line key={i} x1="0" y1={y} x2={PW} y2={y} stroke="var(--c-grid)" strokeWidth="1"
              vectorEffect="non-scaling-stroke" />
          ))}
          {children}
        </svg>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '76px 1fr', gap: 8, marginTop: 6 }}>
        <div />
        <div style={{
          display: 'grid', gridAutoFlow: 'column', gridAutoColumns: '1fr', fontSize: 11,
          color: 'var(--color-neutral-600)',
        }}>
          {xLabels.map((t, i) => <span key={i} style={{ textAlign: 'center' }}>{t}</span>)}
        </div>
      </div>
    </>
  )
}

function Lines({ lines }: { lines: Line[] }) {
  return (
    <>
      {lines.map((s) => s.segs.map((points, i) => (
        <polyline key={s.name + i} points={points} fill="none" stroke={s.color} strokeWidth="1.8"
          strokeDasharray={s.dash} vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
      )))}
    </>
  )
}

function SmallPrint({ children, width = '92ch' }: { children: ReactNode; width?: string }) {
  return (
    <p className="pretty" style={{
      fontSize: 12, color: 'var(--color-neutral-600)', margin: '12px 0 0', maxWidth: width,
    }}>{children}</p>
  )
}

function RankTable({ title, first, rows, bar, yoy = false }: {
  title: string; first: string; rows: RankRow[]; bar: string; yoy?: boolean
}) {
  return (
    <div>
      <div style={{
        fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase',
        color: 'var(--color-accent-700)', marginBottom: 8,
      }}>{title}</div>
      <table className="table" style={{ width: '100%' }}>
        <thead>
          <tr>
            <th>{first}</th><th style={R}>Volume</th><th style={R}>Value</th>
            <th style={R}>$/kg</th><th style={R}>Share</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} style={{ background: r.bg }}>
              <td>
                <div style={{ fontWeight: r.weight as CSSProperties['fontWeight'] }}>{r.name}</div>
                <div style={{ height: 3, background: 'var(--c-track)', marginTop: 5 }}>
                  <div style={{ height: 3, background: bar, width: `${r.pct}%` }} />
                </div>
              </td>
              <td style={NUMR}>{r.kg}</td>
              <td style={NUMR}>{r.usd}</td>
              <td style={NUMR}>{r.uv}</td>
              <td style={NUMR}>
                {r.share}
                {yoy ? <div style={{ fontSize: 11, fontWeight: 600, color: r.tone }}>{r.yoy}</div> : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

const TWO_UP: CSSProperties = {
  display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(380px,1fr))', gap: 24, marginTop: 16,
}

// --------------------------------------------------------- yarn price trends
/** The design's `ypSpec`: a count label read into Ne, ply, process and end
 *  use, so 20/1 is not compared with 20/2 nor hosiery with weaving. */
function ypSpec(s: { count: string; product: string }) {
  const c = String(s.count || '').trim()
  const p = String(s.product || '')
  const m = /(\d+(?:\.\d+)?)\s*(?:s|'s)?\s*(?:\/\s*(\d))?/i.exec(c)
  const ne = m ? +m[1] : null
  const ply = m && m[2] ? +m[2] : 1
  let proc = 'Unspecified'
  if (/\bOE\b|open.?end|rotor/i.test(c)) proc = 'Open-end'
  else if (/compact/i.test(c)) proc = 'Combed compact'
  else if (/comb/i.test(c)) proc = 'Combed'
  else if (/card|kard/i.test(c)) proc = 'Carded'
  const use = /knit|hosier/i.test(p) ? 'Hosiery (knitting)' : /wov|weav/i.test(p) ? 'Weaving' : 'Not stated'
  const plyL = ply === 1 ? 'Single (/1)' : ply === 2 ? 'Double (/2)' : ply + '-ply (/' + ply + ')'
  const procS: Record<string, string> = {
    'Open-end': 'OE', 'Combed compact': 'CC', 'Combed': 'Combed', 'Carded': 'Carded', 'Unspecified': '',
  }
  return {
    ne, ply, proc, use, plyL,
    neL: ne ? ne + 's' : c,
    spec: (ne ? ne + '/' + ply : c) + (procS[proc] ? ' ' + procS[proc] : ''),
  }
}

/** Prices are shown in the unit they are quoted in: whole numbers per
 *  tonne, one decimal in the hundreds, two below that. */
const ypFmt = (v: number, unit: string) => n(v, /mt/i.test(unit) ? 0 : v >= 100 ? 1 : 2)
const ypDate = (iso: string, yr: boolean) =>
  +iso.slice(8, 10) + ' ' + MONTHS[+iso.slice(5, 7) - 1] + (yr ? ' ' + iso.slice(0, 4) : '')

/** The "All …" option of each filter, which is also its name in the state. */
const YP_ALL = {
  country: 'All countries', market: 'All markets', use: 'All uses', ply: 'All ply',
  proc: 'All processes', ne: 'All counts', freq: 'All feeds',
}
type YpFilter = keyof typeof YP_ALL
interface YpState extends Record<YpFilter, string> {
  unit: string | null
  hidden: Record<string, boolean>
  hov: string | null
}
const YP_INIT: YpState = { ...YP_ALL, country: 'India', unit: null, hidden: {}, hov: null }
const YP_RESET: YpState = { ...YP_ALL, unit: null, hidden: {}, hov: null }

type YpNorm = YarnPriceSeries & ReturnType<typeof ypSpec>
/** The filters in cascade order: each one's options are what the earlier
 *  ones leave, read off the normalised series by the field named. */
const YP_STEPS: [keyof YpNorm, YpFilter][] = [
  ['country', 'country'], ['market', 'market'], ['use', 'use'], ['plyL', 'ply'],
  ['proc', 'proc'], ['neL', 'ne'], ['freq', 'freq'],
]

/* Six hues by three line styles — eighteen series apart before a repeat. */
const HUES = ['var(--c-ink)', 'var(--color-accent-500)', '#8a5b3a', 'var(--color-accent-300)',
  'var(--color-neutral-500)', '#b98a5e']
const PAL: [string, string][] = []
for (const st of ['solid', 'dashed', 'dotted']) for (const h of HUES) PAL.push([h, st])
const YPH = 300

/** The design's `ypBuild`: the filter options and picks, the chart, the
 *  hover readout, the legend and the table, from the series and the state. */
function ypBuild(all: YarnPriceSeries[], S: YpState) {
  const uniq = (a: string[]) => [...new Set(a)].sort()
  const norm: YpNorm[] = all.map((s) => ({ ...s, ...ypSpec(s) }))
  let cur = norm
  const pick = { ...YP_ALL }
  const opts = {} as Record<YpFilter, string[]>
  for (const [f, k] of YP_STEPS) {
    let o = uniq(cur.map((s) => String(s[f])))
    if (f === 'neL') o = o.sort((a, b) => parseFloat(a) - parseFloat(b))
    opts[k] = [YP_ALL[k], ...o]
    pick[k] = o.includes(S[k]) ? S[k] : YP_ALL[k]
    cur = cur.filter((s) => pick[k] === YP_ALL[k] || String(s[f]) === pick[k])
  }
  const units = uniq(cur.map((s) => s.unit))
  const unit = S.unit && units.includes(S.unit) ? S.unit : (units[0] || '')
  const sel = cur.filter((s) => s.unit === unit && s.pts.length)

  // A label carries only what tells the selected series apart.
  const multi = (f: (s: YpNorm) => string) => new Set(sel.map(f)).size > 1
  const multiC = multi((s) => s.country)
  const multiM = multi((s) => s.market)
  const multiF = multi((s) => s.freq)
  const multiU = multi((s) => s.use)
  const useS = (u: string) => ({ 'Hosiery (knitting)': 'hosiery', Weaving: 'weaving' } as Record<string, string>)[u] || null
  const labelOf = (s: YpNorm) => [s.spec, multiU ? useS(s.use) : null, multiC ? s.country : null,
    multiM ? s.market : null, multiF ? s.freq.toLowerCase() : null].filter(Boolean).join(' · ')
  const series = sel.map((s, i) => ({
    ...s, color: PAL[i % PAL.length][0], style: PAL[i % PAL.length][1],
    label: labelOf(s), hidden: !!S.hidden[s.key],
  }))
  const vis = series.filter((s) => !s.hidden)

  const dates = uniq(vis.flatMap((s) => s.pts.map((p) => p[0])))
  const t = (iso: string) => Date.parse(iso + 'T00:00:00Z')
  const t0 = dates.length ? t(dates[0]) : 0
  const t1 = dates.length ? t(dates[dates.length - 1]) : 1
  const xOf = (iso: string) => (t1 === t0 ? 500 : (t(iso) - t0) / (t1 - t0) * 1000)
  const vals = vis.flatMap((s) => s.pts.map((p) => p[1]))
  const lo = vals.length ? Math.min(...vals) : 0
  const hi = vals.length ? Math.max(...vals) : 1
  const pad = (hi - lo) * 0.08 || Math.abs(hi) * 0.02 || 1
  const sc = scale(lo - pad, hi + pad, 4)
  const yOf = (v: number) => YPH - (v - sc.lo) / (sc.hi - sc.lo) * YPH
  const lines = vis.map((s) => ({
    key: s.key, color: s.color,
    dash: s.style === 'dashed' ? '7 4' : s.style === 'dotted' ? '2 3' : '0',
    points: s.pts.map((p) => xOf(p[0]).toFixed(1) + ',' + yOf(p[1]).toFixed(1)).join(' '),
    dots: s.pts.length <= 12 ? s.pts.map((p) => ({ x: xOf(p[0]).toFixed(1), y: yOf(p[1]).toFixed(1) })) : [],
  }))

  // About eight x labels, never closer than 90 units, and always the last date.
  const span = (t1 - t0) / 86400000
  const step = Math.max(1, Math.ceil(dates.length / 8))
  const keep: string[] = []
  dates.forEach((d, i) => {
    if (i === dates.length - 1) {
      while (keep.length && xOf(d) - xOf(keep[keep.length - 1]) < 90) keep.pop()
      keep.push(d)
    } else if (i % step === 0 && (!keep.length || xOf(d) - xOf(keep[keep.length - 1]) >= 90)) {
      keep.push(d)
    }
  })
  const xLabels = keep.map((d) => ({
    label: ypDate(d, span > 300), left: xOf(d) / 10 + '%',
    tx: xOf(d) < 40 ? 'translateX(0)' : xOf(d) > 960 ? 'translateX(-100%)' : 'translateX(-50%)',
  }))
  const cols = dates.map((d, i) => {
    const a = i ? (xOf(dates[i - 1]) + xOf(d)) / 2 : 0
    const b = i < dates.length - 1 ? (xOf(d) + xOf(dates[i + 1])) / 2 : 1000
    return { date: d, x: a.toFixed(1), w: Math.max(1, b - a).toFixed(1) }
  })
  const hd = S.hov && dates.includes(S.hov) ? S.hov : null
  const hov = hd ? {
    x: xOf(hd).toFixed(1), left: xOf(hd) / 10 + '%',
    shift: xOf(hd) > 620 ? 'translateX(calc(-100% - 12px))' : 'translateX(12px)',
    title: ypDate(hd, true) + ' · ' + unit,
    rows: vis.flatMap((s) => {
      const p = s.pts.find((q) => q[0] === hd)
      return p ? [{ key: s.key, label: s.label, color: s.color, v: ypFmt(p[1], unit) }] : []
    }),
  } : null

  const pctTxt = (p: number | null) =>
    (p === null ? '–' : (p > 0 ? '▲ ' : p < 0 ? '▼ ' : '') + n(Math.abs(p), 1) + '%')
  const tone = (p: number | null) => (p === null || p === 0 ? 'var(--color-neutral-600)' : p > 0 ? UP : DOWN)
  const rows = series.map((s) => {
    const a = s.pts
    const L = a[a.length - 1]
    const P = a[a.length - 2]
    const F = a[0]
    const d1 = P ? (L[1] / P[1] - 1) * 100 : null
    const dp = a.length > 1 ? (L[1] / F[1] - 1) * 100 : null
    return {
      key: s.key, color: s.color, count: s.spec,
      product: [s.use, s.plyL, s.proc !== 'Unspecified' ? s.proc : null].filter(Boolean).join(' · '),
      where: s.country + ' · ' + s.market, freq: s.freq,
      latest: ypFmt(L[1], unit) + ' ' + unit,
      d1: pctTxt(d1), dTone: tone(d1), dp: pctTxt(dp), pTone: tone(dp), asOf: ypDate(L[0], true),
    }
  })
  const lastAll = all.reduce((m, s) =>
    (s.pts.length && s.pts[s.pts.length - 1][0] > m ? s.pts[s.pts.length - 1][0] : m), '')
  const st = sc.vals.length > 1 ? sc.vals[1] - sc.vals[0] : 1
  const dp = st >= 1 ? 0 : st >= 0.1 ? 1 : 2

  return {
    subline: lastAll ? 'latest quote ' + ypDate(lastAll, true) + ' · ' + all.length + ' series' : 'no price files loaded',
    pick, opts, unit, unitOpts: units.length ? units : ['–'],
    has: vis.length > 0 || series.length > 0, empty: series.length === 0,
    yLabels: sc.vals.map((v) => n(v, dp)), gridY: sc.vals.map((v) => yOf(v).toFixed(1)),
    lines, xLabels, cols, hov,
    legend: series.map((s) => ({ key: s.key, label: s.label, color: s.color, style: s.style, op: s.hidden ? 0.4 : 1 })),
    rows,
  }
}

const EYEBROW: CSSProperties = {
  fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--color-neutral-600)',
}
const YP_FILTERS: { label: string; key: YpFilter }[] = [
  { label: 'Country', key: 'country' }, { label: 'Market', key: 'market' }, { label: 'End use', key: 'use' },
  { label: 'Ply', key: 'ply' }, { label: 'Process', key: 'proc' }, { label: 'Count (Ne)', key: 'ne' },
  { label: 'Frequency', key: 'freq' },
]

/** Quoted market prices by count — the design's "Yarn price trends" panel.
 *  Its series are the page's own request, so the trade payload and the
 *  quotes load and fail independently. */
function YarnPriceTrends() {
  const canUpload = useCan('upload')
  // An editor hands files in on the Data loads page instead.
  const loadElsewhere = useCan('load') && !canUpload
  const { data, error, loading, reload } = useAsync(() => trade.yarnPrices(), [])
  const [S, setS] = useState<YpState>(YP_INIT)
  const [msg, setMsg] = useState('')
  const [busy, setBusy] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  const set = (patch: Partial<YpState>) => setS((s) => ({ ...s, ...patch, hov: null }))
  const v = ypBuild(data?.series ?? [], S)

  const onFile = async (list: FileList | null) => {
    const files = [...(list || [])]
    if (!files.length || busy) return
    setBusy(true)
    try {
      const r = await trade.uploadYarnPrices(files)
      setMsg(r.series ? 'Added ' + r.series + ' series (' + r.kinds.join(' + ') + ')' : 'No prices found in that file')
      reload()
    } catch (e: any) {
      setMsg(e?.message ?? 'Could not read that file')
    } finally {
      setBusy(false)
      if (fileRef.current) fileRef.current.value = ''
    }
  }

  return (
    <Blueprint className="sheet">
      <div style={{
        display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap',
      }}>
        <div>
          <h2 style={{ fontSize: 22, margin: 0 }}>Yarn price trends</h2>
          <div style={{ fontSize: 12, color: 'var(--color-neutral-600)', marginTop: 2 }}>
            Quoted market prices by count · {loading && !data ? 'loading…' : v.subline}
          </div>
        </div>
        <div className="no-print" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {msg ? <span style={{ fontSize: 12, color: 'var(--color-accent-800)' }}>{msg}</span> : null}
          {canUpload ? (
            <label className="btn btn-secondary" style={{ cursor: busy ? 'default' : 'pointer' }}>
              {busy ? 'Reading…' : 'Add price file'}
              <input ref={fileRef} type="file" accept=".xlsx" multiple disabled={busy}
                onChange={(e) => onFile(e.target.files)} style={{ display: 'none' }} />
            </label>
          ) : null}
          {loadElsewhere ? <LoadElsewhere /> : null}
        </div>
      </div>

      <div className="no-print" style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: '12px 16px', marginTop: 16,
      }}>
        {YP_FILTERS.map((f) => (
          <label key={f.key} style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
            <span style={EYEBROW}>{f.label}</span>
            <select className="input" value={v.pick[f.key]}
              onChange={(e) => set({ [f.key]: e.target.value } as Partial<YpState>)}>
              {v.opts[f.key].map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </label>
        ))}
        <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
          <span style={EYEBROW}>Price unit</span>
          <select className="input" value={v.unit} onChange={(e) => set({ unit: e.target.value })}>
            {v.unitOpts.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </label>
        <div style={{ display: 'flex', alignItems: 'flex-end' }}>
          <button className="btn btn-ghost" type="button" onClick={() => setS(YP_RESET)}>Reset filters</button>
        </div>
      </div>

      {error ? (
        <div style={{ padding: '48px 0', textAlign: 'center', fontSize: 13, color: 'var(--color-bad)' }}>{error}</div>
      ) : v.empty ? (
        <div style={{ padding: '48px 0', textAlign: 'center', fontSize: 13, color: 'var(--color-neutral-600)' }}>
          No prices for this selection.
        </div>
      ) : null}

      {v.has ? (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '76px minmax(0,1fr)', gap: 8, marginTop: 18 }}>
            <div className="num" style={{
              display: 'flex', flexDirection: 'column-reverse', justifyContent: 'space-between',
              alignItems: 'flex-end', height: YPH, margin: '-8px 0', fontSize: 11,
              color: 'var(--color-neutral-600)',
            }}>
              {v.yLabels.map((t, i) => <span key={i}>{t}</span>)}
            </div>
            <div onMouseLeave={() => setS((s) => ({ ...s, hov: null }))} style={{ position: 'relative' }}>
              <svg viewBox={`0 0 ${PW} ${YPH}`} preserveAspectRatio="none" style={{
                width: '100%', height: YPH, display: 'block', borderLeft: AXIS_LINE, borderBottom: AXIS_LINE,
              }}>
                {v.gridY.map((y, i) => (
                  <line key={i} x1="0" y1={y} x2={PW} y2={y} stroke="var(--c-grid)" strokeWidth="1"
                    vectorEffect="non-scaling-stroke" />
                ))}
                {v.lines.map((s) => (
                  <g key={s.key}>
                    <polyline points={s.points} fill="none" stroke={s.color} strokeWidth="2"
                      strokeDasharray={s.dash} vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
                    {s.dots.map((p, i) => (
                      <circle key={i} cx={p.x} cy={p.y} r="3.2" fill={s.color} vectorEffect="non-scaling-stroke" />
                    ))}
                  </g>
                ))}
                {v.hov ? (
                  <line x1={v.hov.x} y1="0" x2={v.hov.x} y2={YPH} stroke="var(--c-ink)" strokeOpacity="0.4"
                    strokeWidth="1" vectorEffect="non-scaling-stroke" />
                ) : null}
                {v.cols.map((c) => (
                  <rect key={c.date} x={c.x} y="0" width={c.w} height={YPH} fill="transparent"
                    onMouseEnter={() => setS((s) => (s.hov === c.date ? s : { ...s, hov: c.date }))} />
                ))}
              </svg>
              {v.hov ? (
                <div style={{
                  position: 'absolute', top: 8, left: v.hov.left, transform: v.hov.shift, pointerEvents: 'none',
                  minWidth: 220, padding: '10px 12px', background: 'var(--color-bg)',
                  border: '1px solid var(--color-divider)', boxShadow: 'var(--shadow-md)', fontSize: 12, zIndex: 2,
                }}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>{v.hov.title}</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {v.hov.rows.map((r) => (
                      <div key={r.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ width: 12, height: 3, display: 'block', flex: 'none', background: r.color }} />
                        <span style={{ flex: 1, color: 'var(--color-neutral-700)' }}>{r.label}</span>
                        <span className="num" style={{ fontWeight: 600 }}>{r.v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '76px minmax(0,1fr)', gap: 8, marginTop: 6 }}>
            <div />
            <div style={{ position: 'relative', height: 14, fontSize: 11, color: 'var(--color-neutral-600)' }}>
              {v.xLabels.map((t) => (
                <span key={t.left} style={{ position: 'absolute', left: t.left, transform: t.tx, whiteSpace: 'nowrap' }}>
                  {t.label}
                </span>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 16 }}>
            {v.legend.map((l) => (
              <button key={l.key} type="button" className="legend-toggle" title="Show / hide"
                onClick={() => setS((s) => ({ ...s, hidden: { ...s.hidden, [l.key]: !s.hidden[l.key] } }))}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8, padding: '5px 10px',
                  border: '1px solid var(--color-divider)', background: 'transparent', cursor: 'pointer',
                  font: 'inherit', fontSize: 12, color: 'var(--color-neutral-800)', opacity: l.op,
                }}>
                <span style={{ width: 18, height: 0, borderTop: `3px ${l.style} ${l.color}`, display: 'inline-block' }} />
                {l.label}
              </button>
            ))}
          </div>

          <div className="scroll-x" style={{ marginTop: 18 }}>
            <table className="table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left' }}>Count</th>
                  <th style={{ textAlign: 'left' }}>Country · market</th>
                  <th style={{ textAlign: 'left' }}>Feed</th>
                  <th style={R}>Latest</th>
                  <th style={R}>vs previous</th>
                  <th style={R}>Over period</th>
                  <th style={R}>As of</th>
                </tr>
              </thead>
              <tbody>
                {v.rows.map((r) => (
                  <tr key={r.key}>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <span style={{ display: 'inline-block', width: 10, height: 10, marginRight: 8, background: r.color }} />
                      {r.count}
                      <div style={{ fontSize: 11, color: 'var(--color-neutral-600)', paddingLeft: 18 }}>{r.product}</div>
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--color-neutral-700)' }}>{r.where}</td>
                    <td style={{ fontSize: 12, color: 'var(--color-neutral-700)' }}>{r.freq}</td>
                    <td style={{ ...NUMR, fontWeight: 600 }}>{r.latest}</td>
                    <td style={{ ...NUMR, color: r.dTone }}>{r.d1}</td>
                    <td style={{ ...NUMR, color: r.pTone }}>{r.dp}</td>
                    <td style={{ ...R, fontSize: 12, color: 'var(--color-neutral-600)' }}>{r.asOf}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
      <SmallPrint width="88ch">
        Each line is one exact spec — count, ply (20/1 single vs 20/2 double), process (carded,
        combed, combed compact, open-end) and end use (hosiery vs weaving) — so like is only
        compared with like. "Not stated" means the source didn't say whether the yarn is for
        knitting or weaving. Prices are plotted in the unit they are quoted in; daily and weekly
        feeds stay separate lines. Files added here are saved to the dashboard for everyone.
      </SmallPrint>
    </Blueprint>
  )
}

// -------------------------------------------------------------------- page
export default function Trade({ view = 'yarn', embed = false }: { view?: TradeView; embed?: boolean }) {
  const { data, error, loading, reload } = useAsync(() => trade.payload(), [])
  const cotton = view === 'cotton'
  const [S, setS] = useState<State>({
    tab: cotton ? 'world' : 'trends', span: 120, group: cotton ? 'raw' : 'yarn', metric: 'volume',
    freq: 'annual', pYear: null, wCmd: cotton ? '5201' : '5205', wYear: null,
  })
  const set = (patch: Partial<State>) => setS((s) => ({ ...s, ...patch }))
  // Embedded, the host page supplies the <main>; alone, this does.
  const shell = (children: ReactNode) => (embed ? <>{children}</> : <main style={MAIN_STYLE}>{children}</main>)

  if (error) return shell(<ErrorBox error={error} onRetry={reload} />)
  if (loading && !data) return shell(<Loading what="the trade data" />)
  if (!data) return null
  if (!data.groups?.length || !data.latestMonth) {
    return shell(
      <Empty title="No trade data on file yet">
        Load the UN Comtrade extract with <code>app.cli load-trade</code> and this fills in.
      </Empty>,
    )
  }

  const v = build(data, S, view)
  const groupTabs = (
    <Control label="HS line">
      <Tabs items={v.groups.map((x) => ({ k: x.key, l: x.label }))} value={S.group}
        onPick={(k) => set({ group: k })} />
    </Control>
  )

  const body = (
    <>
      {!embed && !cotton ? (
        <nav className="no-print">
          <Tabs joined={false} items={[{ k: 'trends', l: 'Yarn' }, { k: 'world', l: 'Global trade' }]}
            value={S.tab} onPick={(k) => set({ tab: k as State['tab'] })} />
        </nav>
      ) : null}

      <section className="sheet" style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(200px,1fr))', gap: 16,
      }}>
        {v.kpis.map((k) => (
          <Blueprint key={k.label} style={{ padding: '14px 16px 16px' }}>
            <div style={{
              fontSize: 10, letterSpacing: '0.12em', textTransform: 'uppercase',
              color: 'var(--color-accent-700)',
            }}>{k.label}</div>
            <div className="num" style={{
              fontFamily: 'var(--font-heading)', fontSize: 32, lineHeight: 1.05, marginTop: 8,
            }}>{k.value}</div>
            <div className="num" style={{ fontSize: 12, fontWeight: 600, marginTop: 3, color: k.tone }}>
              {k.delta}
            </div>
            <div style={{ fontSize: 12, color: 'var(--color-neutral-700)', marginTop: 3 }}>{k.note}</div>
          </Blueprint>
        ))}
      </section>

      {S.tab === 'trends' ? (
        <>
        <Blueprint className="sheet">
          <PanelTop title="What a kilo of yarn fetches"
            hint="India's realised cotton yarn export price in rupees per kilo, month by month"
            controls={<Tabs items={[{ k: 60, l: '5y' }, { k: 120, l: '10y' }, { k: 240, l: '20y' }]}
              value={S.span} onPick={(k) => set({ span: k })} />} />
          <Plot yLabels={v.price.yLabels} gridY={v.price.gridY} xLabels={v.price.xLabels}>
            <Lines lines={v.price.lines} />
          </Plot>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px 22px', marginTop: 14 }}>
            {v.price.lines.map((s) => (
              <span key={s.name} style={{
                display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12,
                color: 'var(--color-neutral-800)',
              }}>
                <span style={{ width: 18, height: 2, display: 'inline-block', background: s.color }} />
                {s.name}
                <span className="num" style={{ fontWeight: 600 }}>{s.last}</span>
              </span>
            ))}
          </div>
          <SmallPrint width="88ch">
            A unit value — customs value over net weight — converted at the RBI monthly average
            USD/INR reference rate, so the line carries product mix and the rupee together with the
            market. Recent months' rates are provisional.
          </SmallPrint>
        </Blueprint>
        <YarnPriceTrends />
        </>
      ) : null}

      {S.tab === 'world' ? (
        <>
          <Blueprint className="sheet">
            <PanelTop title={v.vol.title} hint={v.vol.hint} controls={
              <>
                {groupTabs}
                <Control label="Measure">
                  <Tabs items={[{ k: 'volume', l: 'Volume' }, { k: 'value', l: 'Value' }, { k: 'price', l: '$/kg' }]}
                    value={S.metric} onPick={(k) => set({ metric: k as Metric })} />
                </Control>
                <Control label="Frequency">
                  <Tabs items={[{ k: 'annual', l: 'Annual' }, { k: 'monthly', l: 'Monthly' }]}
                    value={S.freq} onPick={(k) => set({ freq: k as Freq })} />
                </Control>
              </>
            } />
            <Plot yLabels={v.vol.yLabels} gridY={v.vol.gridY} xLabels={v.vol.xLabels}>
              {v.vol.bars.map((b, i) => (
                <rect key={i} x={b.x} y={b.y} width={b.w} height={b.h} fill={b.color}>
                  <title>{b.tip}</title>
                </rect>
              ))}
              <Lines lines={v.vol.lines} />
            </Plot>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px 22px', marginTop: 14 }}>
              {v.vol.legend.map((s) => (
                <span key={s.name} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12,
                  color: 'var(--color-neutral-800)',
                }}>
                  <span style={{ width: 12, height: 12, display: 'inline-block', background: s.color }} />
                  {s.name}
                  <span className="num" style={{ fontWeight: 600 }}>{s.last}</span>
                </span>
              ))}
            </div>
            <SmallPrint>{v.vol.note}</SmallPrint>
          </Blueprint>

          <Blueprint className="sheet">
            <div style={{
              display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
              gap: 16, flexWrap: 'wrap',
            }}>
              <h2 style={{ fontSize: 22, margin: 0 }}>{v.annual.title}</h2>
              <div style={{ fontSize: 12, color: 'var(--color-neutral-600)' }}>{v.annual.hint}</div>
            </div>
            <div className="scroll-x" style={{ marginTop: 12 }}>
              <table className="table" style={{ minWidth: 860 }}>
                <thead>
                  <tr>
                    <th>Year</th>
                    <th style={R}>Exports</th><th style={R}>Value</th><th style={R}>$/kg</th>
                    <th style={R}>Imports</th><th style={R}>Value</th><th style={R}>$/kg</th>
                    <th style={R}>Spread</th>
                  </tr>
                </thead>
                <tbody>
                  {v.annual.rows.map((r) => (
                    <tr key={r.year}>
                      <td style={{ fontWeight: 600 }}>{r.year}</td>
                      <td style={NUMR}>{r.xkg}</td>
                      <td style={NUMR}>{r.xusd}</td>
                      <td style={NUMR}>{r.xuv}</td>
                      <td style={{ ...NUMR, color: 'var(--color-neutral-700)' }}>{r.mkg}</td>
                      <td style={{ ...NUMR, color: 'var(--color-neutral-700)' }}>{r.musd}</td>
                      <td style={{ ...NUMR, color: 'var(--color-neutral-700)' }}>{r.muv}</td>
                      <td style={{ ...NUMR, fontWeight: 600 }}>{r.spread}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Blueprint>

          <Blueprint className="sheet">
            <PanelTop title="Where India sits in the world" hint={v.world.hint} controls={
              <>
                <Control label="Commodity">
                  <Tabs items={v.world.cmdTabs} value={S.wCmd} onPick={(k) => set({ wCmd: k, wYear: null })} />
                </Control>
                <Control label="Year">
                  <Tabs items={v.world.years.map((y) => ({ k: y, l: y }))} value={v.world.year}
                    onPick={(k) => set({ wYear: k })} />
                </Control>
              </>
            } />
            <div style={TWO_UP}>
              <RankTable title="Largest exporters" first="Country" rows={v.world.exporters}
                bar="var(--c-bar1)" />
              <RankTable title="Largest importers" first="Country" rows={v.world.importers}
                bar="var(--c-bar3)" />
            </div>
            <SmallPrint width="96ch">{v.world.note}</SmallPrint>
          </Blueprint>

          <Blueprint className="sheet">
            <PanelTop title="Who India trades with" hint={v.partners.hint} controls={
              <>
                {groupTabs}
                <Control label="Year">
                  <Tabs items={v.partners.years.map((y) => ({ k: y, l: y }))} value={v.partners.year}
                    onPick={(k) => set({ pYear: k })} />
                </Control>
              </>
            } />
            <div style={TWO_UP}>
              <RankTable title={v.partners.destTitle} first="Destination" rows={v.partners.dest}
                bar="var(--c-bar1)" yoy />
              <RankTable title={v.partners.srcTitle} first="Origin" rows={v.partners.src}
                bar="var(--c-bar3)" yoy />
            </div>
            <SmallPrint width="96ch">{v.partners.note}</SmallPrint>
          </Blueprint>
        </>
      ) : null}

      <footer className="pretty" style={{
        fontSize: 12, color: 'var(--color-neutral-600)',
        borderTop: '1px solid var(--color-divider)', paddingTop: 14,
        display: 'flex', flexDirection: 'column', gap: 8, maxWidth: '96ch',
      }}>
        <div>
          <strong style={{ color: 'var(--color-neutral-800)' }}>How to read this.</strong> Volumes
          are net weight as declared to customs; a kilotonne is 1,000 tonnes, and raw-cotton lines
          also carry lakh bales at the Indian 170 kg bale. Every price is a unit value, so it moves
          with product mix — counts, combed against carded, contamination grade — as much as with
          the market. It is not a quote for any one contract.
        </div>
        <div>
          <strong style={{ color: 'var(--color-neutral-800)' }}>Exports and imports are measured
          differently, on purpose.</strong> Exports are India's own customs returns. India's import
          filings collapse from 2023 on, so imports here are mirror data — the sum of what every
          other country reports shipping to India. More complete, but valued FOB at the supplier's
          border, so below landed cost at an Indian port, and filed later, which is why the import
          series stops earlier.
        </div>
        <div>
          <strong style={{ color: 'var(--color-neutral-800)' }}>Caveats.</strong> {v.footerCaveat}
        </div>
        <div>
          HS lines: 5201 raw cotton · 5202 cotton waste · 5203 carded or combed cotton · 5205 cotton
          yarn ≥85% cotton, not retail · 5206 cotton yarn &lt;85% cotton · 5207 cotton yarn for
          retail. Sources: UN Comtrade public API; Cotton A Index from the World Bank Pink Sheet,
          monthly nominal USD. Built {data.built}.
        </div>
      </footer>
    </>
  )

  if (embed) return body
  return (
    <main style={MAIN_STYLE}>
      <Subline>{v.periodLine}</Subline>
      {body}
    </main>
  )
}
